import {
	type DcrFieldName,
	EntityType,
	type Hcphco
} from '@/core/graphql/generated/types';

import {
	ADDRESS_COLLECTION,
	ADDRESS_HISTORIC_FIELDS
} from './collections/address.fields';
import {
	AFFILIATION_COLLECTION,
	AFFILIATION_HISTORIC_FIELDS,
	PARENT_HCO_COLLECTION
} from './collections/affiliation.fields';
import {
	LICENSE_COLLECTION,
	LICENSE_HISTORIC_FIELDS
} from './collections/license.fields';
import {
	HCO_FIELDS,
	HCO_HISTORIC_FIELDS,
	HCO_INLINE_LAYOUT
} from './hco.fields';
import {
	HCP_FIELDS,
	HCP_HISTORIC_FIELDS,
	HCP_INLINE_LAYOUT
} from './hcp.fields';
import type {
	EMAILS_FIELD_NAME,
	NAME_GROUP_KEY,
	PROFILE_STATUS_FIELD_KEY
} from './inline';
import type { LabelKey } from './labelKey';
import {
	getSectionIndex,
	getSections,
	type SectionDef,
	type SectionId
} from './sections';
import type {
	CollectionDef,
	CollectionId,
	CommonFieldDef,
	FieldDef,
	FieldKind,
	HistoricFieldDef,
	InlineLayout,
	ItemFieldDef
} from './types';

export type HcpFieldKey = keyof typeof HCP_FIELDS;
export type HcoFieldKey = keyof typeof HCO_FIELDS;
export type EntityFieldKey = HcpFieldKey | HcoFieldKey;
export type InlineFieldKey =
	| EntityFieldKey
	| typeof PROFILE_STATUS_FIELD_KEY
	| typeof NAME_GROUP_KEY
	| typeof EMAILS_FIELD_NAME;

export type FieldEntry = { key: string; def: FieldDef; index: number };
export type ItemFieldEntry = { key: string; def: ItemFieldDef; index: number };

const ENTITY_FIELDS: Record<EntityType, Readonly<Record<string, FieldDef>>> = {
	[EntityType.Hcp]: HCP_FIELDS,
	[EntityType.Hco]: HCO_FIELDS
};

const ENTITY_HISTORIC_FIELDS: Record<EntityType, readonly HistoricFieldDef[]> =
	{
		[EntityType.Hcp]: HCP_HISTORIC_FIELDS,
		[EntityType.Hco]: HCO_HISTORIC_FIELDS
	};

const INLINE_LAYOUTS: Record<EntityType, InlineLayout<Hcphco>> = {
	[EntityType.Hcp]: HCP_INLINE_LAYOUT,
	[EntityType.Hco]: HCO_INLINE_LAYOUT
};

const COLLECTIONS: Record<CollectionId, CollectionDef> = {
	address: ADDRESS_COLLECTION,
	affiliation: AFFILIATION_COLLECTION,
	parentHco: PARENT_HCO_COLLECTION,
	license: LICENSE_COLLECTION
};

const COLLECTION_HISTORIC_FIELDS: Record<
	CollectionId,
	readonly HistoricFieldDef[]
> = {
	address: ADDRESS_HISTORIC_FIELDS,
	affiliation: AFFILIATION_HISTORIC_FIELDS,
	parentHco: AFFILIATION_HISTORIC_FIELDS,
	license: LICENSE_HISTORIC_FIELDS
};

const toEntries = <D>(fields: Readonly<Record<string, D>>) =>
	Object.entries(fields).map(([key, def], index) => ({ key, def, index }));

export const getEntityFields = (entityType: EntityType) =>
	ENTITY_FIELDS[entityType];

export const getFieldEntries = (entityType: EntityType): FieldEntry[] =>
	toEntries(ENTITY_FIELDS[entityType]);

export const getFieldDef = (
	entityType: EntityType,
	key: string
): FieldDef | undefined => ENTITY_FIELDS[entityType][key];

export const getInlineLayout = (entityType: EntityType) =>
	INLINE_LAYOUTS[entityType];

export const getCollection = (id: CollectionId) => COLLECTIONS[id];

export const resolveInlineItemField = (def: ItemFieldDef): ItemFieldDef => ({
	...def,
	label: def.inline?.label ?? def.label,
	required: def.inline?.required ?? def.required
});

export const getCollectionEntries = (id: CollectionId): ItemFieldEntry[] =>
	toEntries(COLLECTIONS[id].fields);

export const getEntityCollections = (entityType: EntityType) =>
	getFieldEntries(entityType).flatMap(({ key, def }) =>
		def.collection
			? [{ key, def, collection: COLLECTIONS[def.collection] }]
			: []
	);

export const getFieldsBySection = (
	entityType: EntityType
): Array<{ section: SectionDef; fields: FieldEntry[] }> => {
	const entries = getFieldEntries(entityType);

	return getSections(entityType).map(section => ({
		section,
		fields: entries.filter(({ def }) => def.section === section.id)
	}));
};

export const resolveFieldKey = (
	fields: Readonly<Record<string, CommonFieldDef>>,
	name: string
): string | undefined => {
	if (name in fields) return name;

	const aliased = Object.entries(fields).find(([, def]) =>
		def.backendNames?.includes(name)
	);
	if (aliased) return aliased[0];

	if (`${name}Id` in fields) return `${name}Id`;

	return undefined;
};

export const toItemFieldId = (collectionKey: string, itemKey: string) =>
	`${collectionKey}.${itemKey}`;

export const resolveUnavailableFieldIds = (
	entityType: EntityType,
	names: readonly string[]
): Set<string> => {
	const ids = new Set<string>();
	const fields = ENTITY_FIELDS[entityType];
	const collections = getEntityCollections(entityType);

	names.forEach(name => {
		const entityKey = resolveFieldKey(fields, name);
		if (entityKey) ids.add(entityKey);

		collections.forEach(({ key, collection }) => {
			const itemKey = resolveFieldKey(collection.fields, name);
			if (itemKey) ids.add(toItemFieldId(key, itemKey));
		});
	});

	return ids;
};

export const fromEnumName = (
	entityType: EntityType,
	enumName: string
): string | undefined => {
	const match = getFieldEntries(entityType).find(({ def }) =>
		def.enumNames?.some(name => name === enumName)
	);

	if (!match) return undefined;

	return match.def.groupWith ?? match.key;
};

export const toEnumNames = (
	entityType: EntityType,
	keys: readonly string[]
): DcrFieldName[] =>
	Array.from(
		new Set(keys.flatMap(key => getFieldDef(entityType, key)?.enumNames ?? []))
	);

export const getFieldLabel = (
	def: Pick<CommonFieldDef, 'label' | 'labelByCountry'>,
	countryId?: string | null
): LabelKey => (countryId && def.labelByCountry?.[countryId]) || def.label;

export type ResolvedHistoryField = {
	label: LabelKey;
	kind?: FieldKind;
	section: SectionId;
	sectionIndex: number;
	order: number;
};

const ROOT_GROUP = 'root';
const ANCHORED_OFFSET = 0.5;
const UNANCHORED_ORDER = 10_000;

const findHistoric = (
	historic: readonly HistoricFieldDef[],
	name: string
): { def: HistoricFieldDef; position: number } | undefined => {
	const position = historic.findIndex(def => def.names.includes(name));
	return position === -1 ? undefined : { def: historic[position], position };
};

type ResolvedInFields = {
	label: LabelKey;
	order: number;
	kind?: FieldKind;
	section?: SectionId;
	sectionKey?: string;
};

const resolveInFields = <D extends CommonFieldDef>(
	fields: Readonly<Record<string, D>>,
	historic: readonly HistoricFieldDef[],
	name: string,
	countryId?: string | null
): ResolvedInFields | undefined => {
	const keys = Object.keys(fields);
	const found = findHistoric(historic, name);

	if (found) {
		const anchor = found.def.anchor;
		const anchorIndex = anchor ? keys.indexOf(anchor) : -1;

		return {
			label: found.def.label,
			kind: found.def.kind,
			section: found.def.section,
			sectionKey: anchor,
			order:
				anchorIndex === -1
					? UNANCHORED_ORDER + found.position
					: anchorIndex + ANCHORED_OFFSET + found.position / 1000
		};
	}

	const key = resolveFieldKey(fields, name);
	if (!key) return undefined;

	const def = fields[key];

	return {
		label: def.historyLabel ?? getFieldLabel(def, countryId),
		kind: def.kind,
		sectionKey: key,
		order: keys.indexOf(key)
	};
};

const collectionKeyFromGroup = (group: string) => group.split(':')[0];

export const getCollectionForGroup = (
	entityType: EntityType,
	group: string
) => {
	if (group === ROOT_GROUP) return undefined;

	const groupKey = collectionKeyFromGroup(group);
	return getEntityCollections(entityType).find(
		({ key, def }) => key === groupKey || def.backendNames?.includes(groupKey)
	);
};

export const resolveHistoryField = (
	entityType: EntityType,
	group: string,
	name: string,
	countryId?: string | null
): ResolvedHistoryField | undefined => {
	const fields = ENTITY_FIELDS[entityType];
	const collections = getEntityCollections(entityType);

	const toResult = (
		resolved: ResolvedInFields,
		fallbackSection: SectionId
	): ResolvedHistoryField => {
		const section = resolved.section ?? fallbackSection;
		return {
			label: resolved.label,
			kind: resolved.kind,
			section,
			sectionIndex: getSectionIndex(entityType, section),
			order: resolved.order
		};
	};

	const resolveInCollection = (collection: (typeof collections)[number]) => {
		const resolved = resolveInFields(
			collection.collection.fields,
			COLLECTION_HISTORIC_FIELDS[collection.collection.id],
			name,
			countryId
		);
		return resolved && toResult(resolved, collection.def.section);
	};

	const groupCollection = getCollectionForGroup(entityType, group);
	if (groupCollection) return resolveInCollection(groupCollection);

	const rootResolved = resolveInFields(
		fields,
		ENTITY_HISTORIC_FIELDS[entityType],
		name,
		countryId
	);

	if (rootResolved) {
		const sectionKey = rootResolved.sectionKey;
		return toResult(
			rootResolved,
			(sectionKey && fields[sectionKey]?.section) || 'name'
		);
	}

	for (const collection of collections) {
		const resolved = resolveInCollection(collection);
		if (resolved) return resolved;
	}

	return undefined;
};
